import { useMutation, useQuery } from 'apollo-3';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';

import { Box, ButtonFilled, Flex, SvgIcon, Text } from 'Components/Base';
import Loading from 'Components/Loading';
import { guidedPracticeEnterSession } from 'GraphQL/mutation/GuidedPractice.mutation';
import { getGuidedPractice } from 'GraphQL/query/GuidedPractice.query';
import { PATHS } from 'Routes/Main/paths';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import ConfirmModal from 'Components/ConfirmModal';
import { SadIcon } from 'Components/Base/Icon/AnywhereSVGIcons';
import { useDisclosure } from 'Util/Hooks';

import Header from '../../Components/Header';
import InformationCard from '../../Components/InformationCard';
import InnerLayout from '../../Components/InnerLayout';
import ThumbnailCard from '../../Components/ThumbnailCard';
import TipCard from '../../Components/TipCard';

const Introduction = () => {
  const history = useHistory();
  const { t } = useTranslation('guidedPractice');
  const { isMobile } = useCompatibleDisplay();
  const { guidedPracticeUserId } = useParams();
  const [isOpenErrorEnterExamRoomModal, openErrorEnterExamRoomModal, closeErrorEnterExamRoomModal] =
    useDisclosure({});

  const { data: guidedPracticeData, loading: guidedPracticeLoading } = useQuery(getGuidedPractice, {
    variables: { guidedPracticeUserId },
    skip: !guidedPracticeUserId,
    fetchPolicy: 'network-only',
  });
  const guidedPractice = guidedPracticeData?.guidedPractice;

  const isContinueExam = guidedPractice?.status === 'IN_PROGRESS';
  const isSubmitExam = guidedPractice?.status === 'END';

  const [enterGuidedPracticeExam, { loading: loadingEnterExamRoom }] = useMutation(
    guidedPracticeEnterSession,
    {
      onCompleted: (data) => {
        if (data?.guidedPracticeEnterSession?.success) {
          const { examSessionId, examPaperId } = data?.guidedPracticeEnterSession?.session;
          closeErrorEnterExamRoomModal();
          history.push(
            `${PATHS.guidedPractice}/${guidedPracticeUserId}/exam-room/${examPaperId}/session/${examSessionId}`
          );
        } else {
          openErrorEnterExamRoomModal();
          console.error('Error entering exam room:', data?.guidedPracticeEnterSession?.message);
        }
      },
      onError: (error) => {
        openErrorEnterExamRoomModal();
        console.error('Error entering exam room:', error);
      },
    }
  );

  const handleBackToGuidedPracticeList = () => {
    closeErrorEnterExamRoomModal();
    history.replace(PATHS.guidedPractice);
  };

  const handleStartExam = () => {
    // NOTED: If exam paper is already created, we can directly navigate to exam room
    if (isContinueExam) {
      history.push(
        `${PATHS.guidedPractice}/${guidedPracticeUserId}/exam-room/${guidedPractice.latestExamPaperId}/session/${guidedPractice.latestExamSessionId}`
      );
    } else {
      enterGuidedPracticeExam({
        variables: {
          guidedPracticeRelationId: guidedPractice?.guidedPracticeRelationId,
          guidedPracticeRelationItemId: guidedPractice?.guidedPracticeRelationItemId,
          guidedPracticeSourceId: guidedPractice?.sourceId,
        },
      });
    }
  };

  useEffect(() => {
    if (!guidedPracticeUserId || (!guidedPractice && !guidedPracticeLoading)) {
      history.replace(PATHS.guidedPractice);
    } else if (isSubmitExam) {
      history.replace(
        `${PATHS.guidedPractice}/${guidedPracticeUserId}/exam-room/${guidedPractice.latestExamPaperId}/session/${guidedPractice.latestExamSessionId}/results`
      );
    }
  }, [guidedPracticeUserId, guidedPractice, guidedPracticeLoading, history, isSubmitExam]);

  if (guidedPracticeLoading) return <Loading width={100} height={100} />;

  return (
    <Flex
      data-testid={'introduction'}
      height={'100vh'}
      overflow={'hidden'}
      flexDirection={'column'}
    >
      <Header onClose={() => history.replace(PATHS.guidedPractice)} />
      <InnerLayout style={{ paddingTop: 32 }}>
        <Flex
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'normal' : 'center'}
          justifyContent={'space-between'}
        >
          <Box data-testid={'greeting'}>
            <Text data-testid={'title'} textStyle={'heading2'} color={'primary'} mb={2}>
              {t('INTRO.GREETING.TITLE')}
            </Text>
            <Text data-testid={'description'} textStyle={'subtitle1'} fontWeight={400}>
              {t('INTRO.GREETING.DESCRIPTION')}
            </Text>
          </Box>
          <Flex
            data-testid={'information'}
            alignItems={'center'}
            gridColumnGap={4}
            mt={isMobile ? 4 : 0}
          >
            <InformationCard
              data-testid={'passage-unit-card'}
              unit={t('INTRO.INFORMATION.PASSAGE_UNIT')}
              icon={SvgIcon.ICON_LIST.BOOK_OUTLINE}
              count={guidedPractice?.articleAmount}
            />
            <InformationCard
              data-testid={'question-unit-card'}
              unit={t('INTRO.INFORMATION.QUESTION_UNIT')}
              icon={SvgIcon.ICON_LIST.QUESTION_MARK_PERSON_ICON}
              count={guidedPractice?.questionAmount}
            />
          </Flex>
        </Flex>
        <Box mt={6}>
          <ThumbnailCard
            title={guidedPractice?.title}
            description={t('INTRO.THUMBNAIL.DESCRIPTION')}
            icon={SvgIcon.ICON_LIST.IDEA}
          />
        </Box>
        <Box mt={6}>
          <TipCard />
        </Box>
        <Flex mt={7} justifyContent={'center'}>
          <ButtonFilled
            data-testid={'start-button'}
            py={4}
            px={6}
            width={198}
            borderRadius={300}
            disabled={loadingEnterExamRoom}
            onClick={handleStartExam}
          >
            {isContinueExam ? t('BUTTON.CONTINUE') : t('BUTTON.START')}
          </ButtonFilled>
        </Flex>
      </InnerLayout>
      <ConfirmModal
        titleText={t('EXAM_ROOM.ERROR_ENTER_EXAM_ROOM.TITLE')}
        contentText={t('EXAM_ROOM.ERROR_ENTER_EXAM_ROOM.DESCRIPTION')}
        primaryText={t('EXAM_ROOM.ERROR_ENTER_EXAM_ROOM.CONFIRM')}
        secondaryText={t('EXAM_ROOM.ERROR_ENTER_EXAM_ROOM.CANCEL')}
        primaryOnClick={handleStartExam}
        disabledPrimaryButton={loadingEnterExamRoom}
        icon={<SadIcon />}
        muiProps={{
          onClose: handleBackToGuidedPracticeList,
          modalPaperStyle: { maxWidth: 336, textAlign: 'center' },
          isActive: isOpenErrorEnterExamRoomModal,
        }}
      />
    </Flex>
  );
};

export default Introduction;
